import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Button, Box, TextField } from '@mui/material';

const primaryButtonSx = {
    background: '#FF6900',
    color: '#fff',
    borderRadius: '50px',
    textTransform: 'none',
    fontWeight: 700,
    px: 3,
    boxShadow: 'none',
    '&:hover': { background: '#c9541e' },
    '&.Mui-disabled': { background: 'rgba(255,105,0,0.38)', color: '#fff' },
};

const secondaryButtonSx = {
    borderRadius: '50px',
    textTransform: 'none',
    fontWeight: 700,
    px: 1.5,
    lineHeight: 1.2,
};

const MAX_NAME_LENGTH = 120;

/**
 * Two-step Duplicate flow for a learning program.
 *
 *  1. `stage === 'confirm'` — name the copy. Prefilled "Copy of {name}" and
 *     editable, because a duplicate almost always wants renaming ("… Term 2",
 *     "… Grade 7") and doing it here saves a trip through the editor.
 *  2. `stage === 'success'` — offer to jump straight into the new draft.
 *
 * `stage === null` renders nothing (both dialogs closed).
 *
 * Deliberately mirrors PublishProgramDialogs: same two-stage shape, same button
 * treatment, so the two row actions feel like one family.
 */
const DuplicateProgramDialogs = ({
    stage,
    duplicating,
    // Confirm stage
    name = '',
    onNameChange,
    onCancel,
    onConfirmDuplicate,
    // Success stage
    duplicatedName = '',
    onPreview,
    onStayOnList,
}) => {
    const trimmedName = String(name).trim();
    // An empty name would land the copy in the list with a blank title, so the
    // action is blocked rather than silently falling back to a default.
    const nameIsValid = trimmedName.length > 0 && trimmedName.length <= MAX_NAME_LENGTH;

    const submitOnEnter = (e) => {
        if (e.key === 'Enter' && nameIsValid && !duplicating) {
            e.preventDefault();
            onConfirmDuplicate();
        }
    };

    return (
        <>
            <Dialog open={stage === 'confirm'} onClose={duplicating ? undefined : onCancel} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    Duplicate this program?
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ textAlign: 'center', color: '#555', mb: 2.5 }}>
                        You'll get an editable draft copy. Assigned schools and classes,
                        learner responses and published status are not copied.
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        label="Name of the copy"
                        value={name}
                        onChange={(e) => onNameChange(e.target.value)}
                        onKeyDown={submitOnEnter}
                        disabled={duplicating}
                        inputProps={{ maxLength: MAX_NAME_LENGTH }}
                        error={!!name && !nameIsValid}
                        helperText={!!name && !nameIsValid ? 'Enter a name for the copy.' : ' '}
                    />
                </DialogContent>
                <DialogActions>
                    <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                        <Button variant="outlined" onClick={onCancel} disabled={duplicating} sx={secondaryButtonSx}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={onConfirmDuplicate}
                            disabled={duplicating || !nameIsValid}
                            sx={primaryButtonSx}
                        >
                            {duplicating ? 'Duplicating...' : 'Duplicate'}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            <Dialog open={stage === 'success'} onClose={onStayOnList} maxWidth="xs" fullWidth>
                <DialogContent sx={{ textAlign: 'center', py: 4, px: 3 }}>
                    <Typography sx={{ fontWeight: 'bold', fontSize: 18, mb: 1 }}>
                        Program duplicated
                    </Typography>
                    <Typography sx={{ color: '#555' }}>
                        &ldquo;{duplicatedName}&rdquo; was created as a draft. Would you like to preview it now?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', justifyContent: 'center', gap: 2, mb: 2 }}>
                        <Button variant="outlined" onClick={onStayOnList} sx={secondaryButtonSx}>
                            Not now
                        </Button>
                        <Button variant="contained" onClick={onPreview} sx={primaryButtonSx}>
                            Preview
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default DuplicateProgramDialogs;
